import RenderManager from "../../rendering/render-manager";
import Network from "../../miniviews/network";
import SlideManager from "../../utils/slide-manager";
import ensureArray from '../../utils/ensure-array';
import AssetLibrary from "../../assets/asset-library";
import getVWKey from "../../helpers/getVWKey";
import { fire } from "../../utils/events";
import generateAnimatedBoundingBox from "../../utils/generate-animated-bbox";
import { iterateTourActionContainers, iterateSlideActionContainers, iterateActions } from '../../utils/iterate-actions';
import chainIterators from '../../utils/chain-iterators';
import ActionReferenceMap from '../../utils/action-ref-map';


const INTERPOLATE_FROM_CURRENT_POSITION = true;
// Start transition animation from object's current position as opposed to the one specified in slide
// animation. Otherwise, if for some reason the object is placed in a different position than specified
// in slide animation, switching slides will cause it to snap at the beginning of the animation.

const identity = new THREE.Matrix4();
const tmpEuler = new THREE.Euler();
export function calcAnimated(object, slideId) {
    const slide = Q3.data.slides[slideId];
    const scaleGate = new THREE.Vector3();
    const positionGate = new THREE.Vector3();

    let animation = null;
    let hadPosition = true, hadScale = true;

    if (slide && (animation = slide.animation[object.uuid])) {
        if (!animation.position) hadPosition = false;
        else positionGate.copy(animation.position)//.applyMatrix4(object.parent ? object.parent.matrixWorld : identity);

        if (!animation.scale) hadScale = false;
        else {
            //   const parentScale = new THREE.Vector3().setFromMatrixScale(object.parent ? object.parent.matrixWorld : identity)
            scaleGate.copy(animation.scale)//.multiply(parentScale);
        }
    }

    if (!hadScale) scaleGate.setFromMatrixScale(object.matrixWorld);
    if (!hadPosition) positionGate.setFromMatrixPosition(object.matrixWorld);

    return { position: positionGate, scale: scaleGate }
}

const DefaultMaterialType = 'pbr';

const MaterialParams = {
    pbr: ["secondaryColor", 'opacity', 'color', 'emissive', 'emissiveIntensity', 'envMapIntensity', 'map', 'transparent'],
    nested: ['opacity', 'color', 'emissive', 'emissiveIntensity', 'envMapIntensity', 'map', 'transparent'],
    basic: ['opacity', 'color', 'emissiveIntensity', 'envMapIntensity', 'map', 'transparent'],
};

const MaterialDefaultValues = { envMapIntensity: 1 };

export class TweenAnimation {

    constructor(slideIndex) {
        this.slideIndex = slideIndex;
        this._playing = false;
    }

    start({ duration = 2.5, onTransitionComplete, prevSlide }) {
        if (typeof (arguments[0]) !== "object" || arguments.length > 1) throw new Error("API Changed");

        this._ended();
        this._playing = true;

        const activeSlideIndex = this.slideIndex;

        prevSlide = typeof prevSlide === "number" ? prevSlide : SlideManager.prevSlide;

        let cut = !areSlidesConnected(activeSlideIndex, prevSlide);
        let cameraDuration = cut ? 0 : duration

        const currentSlide = SlideManager.getSlide(activeSlideIndex);

        const promises = [];
        if (currentSlide.controls.interactionType !== 'asset') {
            const cameraPromise = new Promise((resolve, reject) => {
                RenderManager.activeCamera.updateCamera({
                    duration: cameraDuration,
                    onTransitionComplete: () => {
                        if (onTransitionComplete) {
                            onTransitionComplete();
                        }

                        resolve();
                    },
                });
            });
            promises.push(cameraPromise);
        }

        let actRefMap = null;
        const getActRefMap = () => {
            if (!actRefMap) {
                const tourActContIt = iterateTourActionContainers(Q3.data);
                const slideActContIt = iterateSlideActionContainers(currentSlide);
                const actContIt = chainIterators([tourActContIt, slideActContIt]);
                actRefMap = new ActionReferenceMap(iterateActions(actContIt));
            }

            return actRefMap;
        };

        const isObjectInActions = objectUuid => getActRefMap().isObjectReferredByActions(objectUuid, "unhide3d");
        const isObjectVisible = (objectUuid, animData) => animData.visible !== false || isObjectInActions(objectUuid);

        const currentlyAnimatedObjects = new WeakMap();
        const emptySet = new Set();

        for (const uuid in currentSlide.animation) {
            const animData = currentSlide.animation[uuid];
            const root = Q3.assets.objects[uuid];

            if (root && isObjectVisible(uuid, animData) && !animData.deleted) {
                const action = Q3.mixer.getObjectAction(uuid).activeAction;

                if (action) {
                    const clip = action.getClip();

                    for (const track of clip.tracks) {
                        const { nodeName, propertyName } = THREE.PropertyBinding.parseTrackName(track.name);
                        const object = THREE.PropertyBinding.findNode(root, nodeName);

                        if (object !== null) {
                            let objectProperties = currentlyAnimatedObjects.get(object);
                            if (!objectProperties) {
                                objectProperties = new Set();
                                currentlyAnimatedObjects.set(object, objectProperties);
                            }

                            objectProperties.add(propertyName);
                        }
                    }
                }
            }
        }

        const empty = {};
        const objectsContainingAnimations = {};

        Object.keys(currentSlide.animation).forEach(objectUuid => {
            const mesh = Q3.assets.objects[objectUuid] || Q3.assets.lights[objectUuid];

            const currAnimation = currentSlide.animation[objectUuid];
            const prevAnimation = cut ? currAnimation : Q3.data.slides[prevSlide].animation[objectUuid];

            const currObjectAnimation = currAnimation && "animation" in currAnimation
                ? currAnimation.animation
                : Q3.data.assets.objects[objectUuid] && "animation" in Q3.data.assets.objects[objectUuid]
                    ? Q3.data.assets.objects[objectUuid].animation
                    : null;

            const prevObjectAnimation = prevAnimation && "animation" in prevAnimation
                ? prevAnimation.animation
                : Q3.data.assets.objects[objectUuid] && "animation" in Q3.data.assets.objects[objectUuid]
                    ? Q3.data.assets.objects[objectUuid].animation
                    : null;

            [currObjectAnimation, prevObjectAnimation].forEach(objectAnimation => {
                if (objectAnimation && objectAnimation.uuid && isObjectVisible(objectUuid, currAnimation)) {
                    const animation = AssetLibrary.animations[objectAnimation.uuid];

                    if (objectAnimation.isPreset) {
                        const nodeName = THREE.PropertyBinding.sanitizeNodeName(mesh.name);

                        objectsContainingAnimations[nodeName] = objectsContainingAnimations[nodeName] || {};

                        switch (objectAnimation.preset.type) {
                            case "anim-spin":
                                objectsContainingAnimations[nodeName].quaternion = true;
                                break;
                            case "anim-pop-in":
                                objectsContainingAnimations[nodeName].scale = true;
                                break;
                            case "anim-slide-in":
                                objectsContainingAnimations[nodeName].position = true;
                                break;
                            case "anim-assemble":
                                objectsContainingAnimations[nodeName].position = true;
                                break;
                            default: throw new Error(`Unsupported animation type: ${type}`);
                        }
                    } else if (animation) {
                        animation.tracks.forEach(track => {
                            const { nodeName, propertyName } = THREE.PropertyBinding.parseTrackName(track.name);

                            objectsContainingAnimations[nodeName] = objectsContainingAnimations[nodeName] || {};
                            objectsContainingAnimations[nodeName][propertyName] = true;
                        });
                    }
                }
            });
        });

        Object.keys(currentSlide.animation).forEach(objectUuid => {
            const meshData = Q3.data.assets.objects[objectUuid] || Q3.data.assets.lights[objectUuid];
            const mesh = Q3.assets.objects[objectUuid] || Q3.assets.lights[objectUuid];

            const currAnimation = currentSlide.animation[objectUuid];
            const prevAnimation = cut ? currAnimation : Q3.data.slides[prevSlide].animation[objectUuid];

            if (mesh && mesh.isObject3D) {
                const matUuids = meshData.material ? ensureArray(meshData.material) : [];
                // const materialsData = matUuids.map(matUuid => Q3.data.assets.materials[matUuid]);
                const currMatAnimation = matUuids.map(matUuid => (currentSlide.materialAnimation || empty)[matUuid] || empty);
                const prevMatAnimation = cut
                    ? currMatAnimation
                    : matUuids.map(matUuid => (Q3.data.slides[prevSlide].materialAnimation || empty)[matUuid] || empty);

                const getMaterialValue = (material, matDataArray, param) => {
                    for (let container of matDataArray) {

                        let value = container[param];
                        if (value !== undefined && value !== null) return value;
                    }

                    return material[param];
                };

                const vpType = getVWKey();
                const currCameraData = vpType === "sizeSm" && currAnimation[vpType]
                    ? currAnimation[vpType]
                    : currAnimation;

                const prevCameraData = vpType === "sizeSm" && prevAnimation[vpType]
                    ? prevAnimation[vpType]
                    : prevAnimation;

                const currOptions = {
                    position: currCameraData.position,
                    rotation: currCameraData.rotation && new THREE.Quaternion().setFromEuler(
                        tmpEuler.set(
                            currCameraData.rotation.x,
                            currCameraData.rotation.y,
                            currCameraData.rotation.z,
                            currCameraData.rotation.order || mesh.rotation.order
                        )),
                    scale: currCameraData.scale,
                    repeat: currAnimation.repeat ? -1 : 0,
                };

                const wasVisible = isVisible(objectUuid, prevSlide);
                const willAppear = !wasVisible && isVisible(objectUuid, activeSlideIndex);

                const prevOptions = willAppear
                    ? {
                        position: currCameraData.position,
                        rotation: currCameraData.rotation && new THREE.Quaternion().setFromEuler(
                            tmpEuler.set(
                                currCameraData.rotation.x,
                                currCameraData.rotation.y,
                                currCameraData.rotation.z,
                                currCameraData.rotation.order || mesh.rotation.order
                            )),
                        scale: currCameraData.scale
                    }
                    : INTERPOLATE_FROM_CURRENT_POSITION
                        ? {
                            position: mesh.position,
                            rotation: mesh.rotation,
                            scale: mesh.scale,
                        }
                        : {
                            position: prevCameraData.position,
                            rotation: prevCameraData.rotation && new THREE.Quaternion().setFromEuler(
                                tmpEuler.set(
                                    prevCameraData.rotation.x,
                                    prevCameraData.rotation.y,
                                    prevCameraData.rotation.z,
                                    prevCameraData.rotation.order || mesh.rotation.order
                                )),
                            scale: prevCameraData.scale,
                        };

                const currMatOptions = [], prevMatOptions = [];
                for (let [matIndex, matUuid] of matUuids.filter(u => Q3.data.assets.materials[u]).entries()) {
                    let matData = Q3.data.assets.materials[matUuid];
                    let material = Q3.assets.materials[matUuid];

                    const materialParams = MaterialParams[matData.materialType || DefaultMaterialType];
                    let currValues = {}, prevValues = {};

                    const currMatDataArray = [currMatAnimation[matIndex], currAnimation, matData, MaterialDefaultValues];
                    const prevMatDataArray = [prevMatAnimation[matIndex], prevAnimation, matData, MaterialDefaultValues];
                    for (let param of materialParams) {
                        currValues[param] = getMaterialValue(material, currMatDataArray, param);
                        prevValues[param] = getMaterialValue(material, prevMatDataArray, param);
                    }

                    currMatOptions[matIndex] = currValues;
                    prevMatOptions[matIndex] = prevValues;
                }

                if (mesh.isStar) {
                    currOptions.temperature = currAnimation.temperature || mesh.temperature;
                    prevOptions.temperature = prevAnimation.temperature || mesh.temperature;

                    const tweenStart = {
                        temperature: prevOptions.temperature,
                    };

                    let tweenFinish;
                    const promise = new Promise((resolve, reject) => {
                        tweenFinish = {
                            temperature: currOptions.temperature,
                            ease: currAnimation.ease === "customjetease" ? Power0.easeNone : Power2.easeOut,
                            onComplete: () => {
                                resolve();
                            },
                        };
                    });
                    promises.push(promise);

                    TweenMax.fromTo(mesh, duration, tweenStart, tweenFinish);
                } else if (mesh.isProceduralObject) {

                    currOptions.color = currAnimation.color || mesh.color;
                    prevOptions.color = prevAnimation.color || mesh.color;

                    currOptions.textColor = currAnimation.textColor || mesh.textColor;
                    prevOptions.textColor = prevAnimation.textColor || mesh.textColor;

                    currOptions.edgeColor = currAnimation.edgeColor || mesh.edgeColor;
                    prevOptions.edgeColor = prevAnimation.edgeColor || mesh.edgeColor;

                    currOptions.fontSize = currAnimation.fontSize || mesh.fontSize;
                    prevOptions.fontSize = prevAnimation.fontSize || mesh.fontSize;

                    currOptions.opacity = "opacity" in currAnimation ? currAnimation.opacity : 1;
                    prevOptions.opacity = "opacity" in prevAnimation ? prevAnimation.opacity : 1;

                    const tweenStart = {
                        opacity: prevOptions.opacity,
                        color: prevOptions.color,
                        textColor: prevOptions.textColor,
                        edgeColor: prevOptions.edgeColor,
                        fontSize: prevOptions.fontSize
                    };

                    let tweenFinish;
                    const promise = new Promise((resolve, reject) => {
                        tweenFinish = {
                            opacity: currOptions.opacity,
                            color: currOptions.color,
                            textColor: currOptions.textColor,
                            edgeColor: currOptions.edgeColor,
                            fontSize: currOptions.fontSize,

                            ease: currAnimation.ease === "customjetease" ? Power0.easeNone : Power2.easeOut,

                            onComplete: () => {
                                resolve();
                            },
                        };
                    });
                    promises.push(promise);

                    TweenMax.fromTo(mesh, duration, tweenStart, tweenFinish);

                }

                if (currOptions.transparent === false) currOptions.opacity = 1;
                if (prevOptions.transparent === false) prevOptions.opacity = 1;

                function isAnimated(propery) {
                    if (mesh.name === undefined) return false;

                    const name = THREE.PropertyBinding.sanitizeNodeName(mesh.name);
                    if (!objectsContainingAnimations[name]) return false;
                    return objectsContainingAnimations[name][propery];
                }

                if (!isAnimated("position")) {
                    if (prevOptions.position && currOptions.position) {
                        const promise = new Promise((resolve, reject) => {
                            TweenMax.fromTo(mesh.position, duration, {
                                x: prevOptions.position.x,
                                y: prevOptions.position.y,
                                z: prevOptions.position.z
                            }, {
                                x: currOptions.position.x,
                                y: currOptions.position.y,
                                z: currOptions.position.z,
                                repeat: currOptions.repeat,
                                ease: currAnimation.ease === "customjetease" ? Power0.easeNone : Power2.easeOut,
                                onUpdate() {
                                    if (!Q3.selected) return;
                                    if ((Q3.selected.type === "DirectionalLight" ||
                                        Q3.selected.type === "SpotLight") &&
                                        Q3.selected.castShadow === false)
                                        Q3.selected.userData.helper.update();
                                },
                                onComplete: () => {
                                    resolve();
                                },
                            });
                        });
                        promises.push(promise);
                    }
                } else {
                    const animProps = currentlyAnimatedObjects.get(mesh) || emptySet;
                    if (!animProps.has("position")) {
                        mesh.position.copy(currOptions.position);
                    }
                }

                if (!isAnimated("quaternion") && !isAnimated("rotation")) {
                    if (prevOptions.rotation && currOptions.rotation) {
                        const promise = new Promise((resolve, reject) => {
                            TweenMax.fromTo(
                                { lerp: 0 },
                                duration,
                                { lerp: 0 }, {
                                lerp: 1,
                                repeat: currOptions.repeat,
                                ease: currAnimation.ease === "customjetease" ? Power0.easeNone : Power2.easeOut,
                                onUpdate() {
                                    RenderManager.shadowMap.needsUpdate = true;
                                    RenderManager.activeCamera.update = true;

                                    mesh
                                        .quaternion
                                        .copy(prevOptions.rotation)
                                        .slerp(currOptions.rotation, this.target.lerp);
                                },
                                onComplete: () => {
                                    resolve();
                                },
                            });
                        });
                        promises.push(promise);
                    }
                } else {
                    const animProps = currentlyAnimatedObjects.get(mesh) || emptySet;
                    if (!(animProps.has("rotation") || animProps.has("quaternion"))) {
                        const { x, y, z, order } = currOptions.rotation;
                        mesh.rotation.set(x, y, z, order);
                    }
                }

                if (!isAnimated("scale")) {
                    if (prevOptions.scale && currOptions.scale) {
                        const promise = new Promise((resolve, reject) => {
                            TweenMax.fromTo(mesh.scale, duration, {
                                x: prevOptions.scale.x,
                                y: prevOptions.scale.y,
                                z: prevOptions.scale.z
                            }, {
                                x: currOptions.scale.x,
                                y: currOptions.scale.y,
                                z: currOptions.scale.z,
                                repeat: currOptions.repeat,
                                ease: currAnimation.ease === "customjetease" ? Power0.easeNone : Power2.easeOut,
                                onComplete: () => {
                                    resolve();
                                },
                            });
                        });
                        promises.push(promise);
                    }
                } else {
                    const animProps = currentlyAnimatedObjects.get(mesh) || emptySet;
                    if (!animProps.has("scale")) {
                        mesh.scale.copy(currOptions.scale);
                    }
                }

                if (mesh.material) {
                    for (let [matIndex, material] of ensureArray(mesh.material).entries()) {
                        const thisMatPrevOptions = prevMatOptions[matIndex];

                        if (!prevMatOptions) continue;

                        const thisMatCurrOptions = currMatOptions[matIndex];

                        if (!thisMatCurrOptions) continue;

                        const tweenStart = {
                            opacity: thisMatPrevOptions.opacity,
                            color: thisMatPrevOptions.color,
                            secondaryColor: thisMatPrevOptions.secondaryColor,
                            emissive: thisMatPrevOptions.emissive,
                            emissiveIntensity: thisMatPrevOptions.emissiveIntensity,
                            envMapIntensity: thisMatPrevOptions.envMapIntensity
                        };

                        let tweenFinish;
                        const promise = new Promise((resolve, reject) => {
                            tweenFinish = {
                                opacity: thisMatCurrOptions.opacity,
                                color: thisMatCurrOptions.color,
                                secondaryColor: thisMatCurrOptions.secondaryColor,
                                emissive: thisMatCurrOptions.emissive,
                                emissiveIntensity: thisMatCurrOptions.emissiveIntensity,
                                envMapIntensity: thisMatCurrOptions.envMapIntensity,
                                repeat: thisMatCurrOptions.repeat,
                                ease: currAnimation.ease === "customjetease" ? Power0.easeNone : Power2.easeOut,
                                onStart() {
                                    material.transparent = thisMatCurrOptions.transparent || thisMatPrevOptions.transparent || false;
                                },
                                onComplete() {
                                    material.transparent = thisMatCurrOptions.transparent || false;
                                    resolve();
                                }
                            };
                        });
                        promises.push(promise);

                        Object.keys(tweenFinish).forEach(key => {
                            if (tweenFinish[key] === undefined && tweenStart[key] === undefined) {
                                delete tweenFinish[key];
                                delete tweenStart[key];
                            }
                        });

                        TweenMax.fromTo(material, duration, tweenStart, tweenFinish);

                        // if (prevOptions.map !== thisMatCurrOptions.map) {
                        //     if (thisMatCurrOptions.map) {
                        //         material.map = AssetLibrary.textures[thisMatCurrOptions.map];
                        //     } else {
                        //         material.map = null;
                        //     }

                        //     if (!!thisMatCurrOptions.map !== !!prevOptions.map)
                        //         material.needsUpdate = true;
                        // }
                    }
                }
            }

        });

        Promise.all(promises).then(() => {
            this._ended();

            // Updates SkinnedMesh objects boundingBox after finished slide load.
            Object.keys(currentSlide.animation).forEach(objectUuid => {
                const mesh = Q3.assets.objects[objectUuid];

                if (mesh instanceof THREE.SkinnedMesh) generateAnimatedBoundingBox(mesh);
            });
        });
    }

    kill() {
        {
            const currentSlide = SlideManager.getSlide(this.slideIndex);

            if (currentSlide) {
                Object.keys(currentSlide.animation).forEach(key => {
                    const currMesh = Q3.assets.objects[key] || Q3.assets.lights[key];
                    if (currMesh) {
                        if (currentSlide.animation[key].stop === false) {
                        } else {
                            TweenMax.killTweensOf(currMesh.position);
                            TweenMax.killTweensOf(currMesh.rotation);
                            TweenMax.killTweensOf(currMesh.scale);
                            TweenMax.killTweensOf(currMesh.material);
                        }
                    }
                });
            }

            this._ended();
        }
    }

    _ended() {
        if (this._playing) {
            this._playing = false;
            fire('slide_transition_animation_finished');
        }
    }
}

function areSlidesConnected(slide0, slide1) {
    let bothVisible = Q3.data.slides[slide0].visible && Q3.data.slides[slide1].visible;
    if (!bothVisible) return false;

    return !visibleSlidesBetween(slide0, slide1) || linksBetweenSlides(slide0, slide1) || linksBetweenSlides(slide1, slide0);
}

function visibleSlidesBetween(slide0, slide1) {
    let minIndex = Math.min(slide0, slide1);
    let maxIndex = Math.max(slide0, slide1);

    for (let betweenIndex = minIndex + 1; betweenIndex < maxIndex; betweenIndex++) {

        if (Q3.data.slides[betweenIndex].visible) {

            return true;

        }

    }

    return false;
}

function linksBetweenSlides(fromSlide, toSlide) {
    let { layers2D } = Q3.data.layers;
    let slideLayers = Q3.data.slides[fromSlide].layers2D;

    for (let uuid in slideLayers) {
        if (slideLayers[uuid] && layers2D[uuid]) {
            let objects = layers2D[uuid].objects;
            let objectData = objects && objects[Object.keys(objects)[0]];
            if (objectData && objectData.action) {
                for (let action of objectData.action) {
                    if (action && action.value && action.systemAction === 'navigate' && action.value.slide === toSlide) {
                        return true;
                    }
                }
            }
        }
    }

    return false;
}

function isVisible(objectUuid, slideIndex) {
    const slide = SlideManager.getSlide(slideIndex);
    const objects = Q3.data.assets.objects;

    let parent = objects[objectUuid];
    let parentData = slide.animation[objectUuid];
    let visible = parentData.visible !== false && parentData.deleted !== true;

    while (visible && parent && parent.parent) {
        parentData = slide.animation[parent.parent];
        parent = objects[parent.parent];
        visible = visible && parentData?.visible !== false && parentData?.deleted !== true;
    }

    return visible;
}